'use client'

import { useCallback } from 'react'
import { useRouter } from 'next/navigation'

export interface SamanthaNavigation {
  route: string
  params?: {
    channelId?: string
    postId?: string
    courseId?: string
    lessonId?: string
    timecode?: number
    pageId?: string
    funnelId?: string
    openPost?: boolean
    tab?: string
    niche?: string
    topic?: string
    autoGenerate?: boolean | string
    handoffId?: string
    runId?: string
    discovery?: string
    skillAgentId?: string
    skillAgentSlug?: string
    skillName?: string
    platform?: string
    tone?: string
    prompt?: string
    style?: string
    script?: string

    // Creator Studio project flow
    projectId?: string
    step?: string
    fromSkill?: string
    source?: string

    // allow future params without losing them
    [key: string]: string | number | boolean | undefined
  }
}

/**
 * Hook that executes navigation commands from Samantha without closing the chat.
 * Dispatches custom events so pages can react (e.g., open a post modal).
 */

function buildQueryString(params?: SamanthaNavigation['params']) {
  const search = new URLSearchParams()

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return

    if (typeof value === 'boolean') {
      if (value) search.set(key, 'true')
      return
    }

    search.set(key, String(value))
  })

  return search.toString()
}

export function useSamanthaNavigation() {
  const router = useRouter()

  const executeNavigation = useCallback(
    (nav: SamanthaNavigation) => {
      if (!nav?.route) return

      // If navigating to community with a specific channel + post
      if (nav.route.startsWith('/community')) {
        if (nav.params?.channelId) {
          window.dispatchEvent(
            new CustomEvent('samantha:set-channel', {
              detail: {
                channelId: nav.params.channelId,
              },
            })
          )
        }

        router.push('/community')

        // After navigation, dispatch event to open the post modal
        if (nav.params?.postId || nav.params?.openPost) {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('samantha:open-post', {
                detail: {
                  postId: nav.params?.postId,
                  channelId: nav.params?.channelId,
                },
              })
            )
          }, 300)
        }
        return
      }

      // If navigating to creator-studio, preserve every param returned by Samantha.
      if (nav.route.startsWith('/creator-studio')) {
        const qs = buildQueryString(nav.params)
        router.push(`/creator-studio${qs ? `?${qs}` : ''}`)
        return
      }

      // If navigating to funnel-builder
      if (nav.route.startsWith('/funnel-builder')) {
        const params = new URLSearchParams()
        if (nav.params?.funnelId) params.set('funnelId', nav.params.funnelId)
        if (nav.params?.pageId) params.set('pageId', nav.params.pageId)
        const qs = params.toString()
        router.push(`/funnel-builder${qs ? `?${qs}` : ''}`)
        return
      }

      // If navigating to a course lesson with timecode
      if (nav.route.includes('/lesson/')) {
        const params = new URLSearchParams()

        if (nav.params?.timecode != null) {
          params.set('t', String(nav.params.timecode))
        }

        const qs = params.toString()
        router.push(`${nav.route}${qs ? `?${qs}` : ''}`)

        if (nav.params?.timecode != null) {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('samantha:seek-video', {
                detail: { timecode: nav.params!.timecode },
              })
            )
          }, 1500)
        }

        return
      }

      // Default navigation
      router.push(nav.route)
    },
    [router]
  )

  return { executeNavigation }
}